import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../constants/extraction";

const STORAGE_KEY_PREFIX = "playersPageSize";

/** localStorage key for the players-list page size of a specific organization. */
const getStorageKey = (organizationId: string): string => {
  return `${STORAGE_KEY_PREFIX}_${organizationId}`;
};

/**
 * Only the offered sizes are ever accepted, in or out of storage.
 *
 * The API clamps `pageSize` to 100 and silently substitutes its own default for
 * anything outside 1..100, so a hand-edited localStorage value would otherwise
 * produce a page whose size does not match the one the selector is showing.
 * Validating here keeps the control and the request telling the same story.
 */
const isPageSize = (value: unknown): value is number => {
  return (
    typeof value === "number" &&
    (PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
  );
};

/**
 * Read the persisted players-list page size for an organization. Falls back to
 * the shared default when there is no org, no stored value, or a value that
 * isn't one of the offered sizes.
 */
export const getStoredPageSize = (organizationId: string | null): number => {
  if (!organizationId) {
    return DEFAULT_PAGE_SIZE;
  }

  try {
    const stored = localStorage.getItem(getStorageKey(organizationId));
    if (stored === null) {
      return DEFAULT_PAGE_SIZE;
    }

    const parsed = Number(stored);
    return isPageSize(parsed) ? parsed : DEFAULT_PAGE_SIZE;
  } catch (error) {
    console.warn("Failed to load players page size from localStorage:", error);
    return DEFAULT_PAGE_SIZE;
  }
};

/**
 * Persist the players-list page size for an organization. Unknown sizes are
 * dropped rather than stored, so a bad value can never be read back later.
 */
export const savePageSize = (
  organizationId: string | null,
  pageSize: number
): void => {
  if (!organizationId || !isPageSize(pageSize)) {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(organizationId), String(pageSize));
  } catch (error) {
    console.warn("Failed to save players page size to localStorage:", error);
  }
};

/**
 * Clear every organization's stored page size. Called on logout so a preference
 * never leaks across users on a shared machine.
 */
export const clearAllPageSizes = (): void => {
  try {
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn(
      "Failed to clear all players page sizes from localStorage:",
      error
    );
  }
};
